import { AffiliateEarnings, PaymentSettings, ShowHeader } from "@/components";
import { useUIStore } from "@/stores";
import { AffiliateStatus } from "@/types";
import { Button, Divider, Grid, Link, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Loading } from "react-admin";
import { useParams } from "react-router-dom";
import { AddAffiliateUser, ConfirmChangeAffiliateStatus } from "../../modals";
import { useGetAffiliateUser } from "../../queries";
import { UpdatePaymentSettings } from "@/modals";
import { AffiliateAcquiredUsersTable } from "../affiliate-acquired-users-table";
import { UpdateTrialOffer } from "../update-trial-offer";

const AffiliateShow: FC = () => {
  const { id } = useParams();
  const { openModal } = useUIStore();

  const { affiliate, isAffiliateLoading } = useGetAffiliateUser(id ?? "");

  if (isAffiliateLoading || !affiliate) {
    return <Loading />;
  }

  return (
    <Stack direction={"column"} p={2}>
      <ShowHeader
        title="Affiliate"
        rightAddon={
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              openModal(ConfirmChangeAffiliateStatus, {
                title:
                  affiliate.status === AffiliateStatus.ACTIVE
                    ? "Affiliate Deactivation"
                    : "Affiliate Activation",
                affiliateId: affiliate.id,
                prevStatus: affiliate.status,
              })
            }
          >
            {affiliate.status === AffiliateStatus.ACTIVE
              ? "Deactivate"
              : "Activate"}
          </Button>
        }
      />

      <Grid spacing={4} item xs={12}>
        <Typography variant="h6" fontWeight="bold">
          {affiliate.fullName}
        </Typography>

        <Divider sx={{ margin: "16px 0" }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Email Address
            </Typography>
            <Typography variant="subtitle2">{affiliate.email}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Web or Social
            </Typography>
            <Typography variant="subtitle2">
              <Link href={affiliate.socialUrl} target="_blank">
                Link
              </Link>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Phone Number
            </Typography>
            <Typography variant="subtitle2">{affiliate.phoneNumber}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Earning
            </Typography>
            <Typography variant="subtitle2">
              {affiliate.earnings / 100}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Woman Referral Hits
            </Typography>
            <Typography variant="subtitle2">
              {affiliate.referralsCounts?.muse ?? 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Man Referral Hits
            </Typography>
            <Typography variant="subtitle2">
              {affiliate.referralsCounts?.alpha ?? 0}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ margin: "16px 0" }} />

      <PaymentSettings
        alphaReferralPaymentBps={affiliate.alphaReferralPaymentBps}
        museReferralPaymentCents={affiliate.museReferralPaymentCents}
        bankAccountCredentials={affiliate.bankAccountCredentials}
        rightAddon={
          <Button
            style={{ height: "fit-content" }}
            variant="contained"
            color="primary"
            onClick={() =>
              openModal(UpdatePaymentSettings, {
                title: "Update Payment Settings",
                affiliateId: affiliate.id,
                apiValues: {
                  alphaReferralPaymentBps: (
                    affiliate.alphaReferralPaymentBps / 100
                  ).toString(),
                  museReferralPaymentCents: (
                    affiliate.museReferralPaymentCents / 100
                  ).toString(),
                  bankAccountCredentials: affiliate.bankAccountCredentials,
                },
              })
            }
          >
            Update
          </Button>
        }
      />
      {id && (
        <UpdateTrialOffer
          affiliateId={id}
          trialOfferAmountPrev={affiliate.trialOfferAmount}
          trialOfferUnitPrev={affiliate.trialOfferUnit}
        />
      )}

      <Stack spacing={4} mt={4}>
        <AffiliateEarnings affiliateId={affiliate.id}/>
        {id && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Acquired Users</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  openModal(AddAffiliateUser, {
                    title: "Add User",
                    affiliateId: affiliate.id,
                  })
                }
              >
                Add User
              </Button>
            </Stack>
            <AffiliateAcquiredUsersTable userId={id} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export { AffiliateShow };
